import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

export function usePushNotifications(token) {
  const [permission, setPermission] = useState(Notification.permission);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        const existing = await reg.pushManager.getSubscription();
        setSubscribed(!!existing);
      } catch (_) {}
    })();
  }, [token]);

  const subscribe = useCallback(async () => {
    if (!token) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      alert('Push notifications are not supported in this browser.');
      return;
    }
    setLoading(true);
    try {
      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return;

      // Get VAPID public key
      const { data } = await axios.get(`${API}/api/push/public-key`);
      const applicationServerKey = urlBase64ToUint8Array(data.publicKey);

      // Register SW and subscribe
      const reg = await navigator.serviceWorker.register('/sw.js');
      const pushSub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      // Save subscription to backend
      const subJson = pushSub.toJSON();
      await axios.post(`${API}/api/push/subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      }, { headers: { Authorization: `Bearer ${token}` } });

      setSubscribed(true);
    } catch (err) {
      console.error('Push subscribe error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const unsubscribe = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (!reg) return;
      const pushSub = await reg.pushManager.getSubscription();
      if (!pushSub) return;
      const subJson = pushSub.toJSON();
      await axios.delete(`${API}/api/push/unsubscribe`, {
        data: { endpoint: subJson.endpoint, keys: subJson.keys },
        headers: { Authorization: `Bearer ${token}` },
      });
      await pushSub.unsubscribe();
      setSubscribed(false);
    } catch (err) {
      console.error('Push unsubscribe error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  return { permission, subscribed, loading, subscribe, unsubscribe };
}
